import DateVO from '../../../vo/DateVO'
import { DayOfWeek, mapDayOfWeekToNumber } from '../../../lib/functions/report'

export enum GanttDisplayUnit {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
}

export type DisplayRange = {
  start: DateVO
  end: DateVO
}

export type GanttDisplayOptions = {
  showTypeIcon: boolean
  showProgressRate: boolean
  showName: boolean
  showAssignee: boolean
  showStatus: boolean
  showProgressLine: boolean
  showMilestoneName: boolean
  milestoneNameResetSeq: number
  lockRange: boolean
}

export type SerializedGanttParameter = {
  unit: GanttDisplayUnit
  startDay: DayOfWeek
  range: { start: number; end: number }
  displayOptions?: GanttDisplayOptions
}

const MAX_DISPLAY_RANGE_DAYS = 1826

export class GanttParameterVO {
  public readonly unit: GanttDisplayUnit
  public readonly startDay: DayOfWeek
  public readonly range: DisplayRange
  public readonly displayOptions: GanttDisplayOptions

  constructor(
    unit: GanttDisplayUnit,
    startDay: DayOfWeek,
    range: DisplayRange,
    displayOptions?: GanttDisplayOptions
  ) {
    this.unit = unit
    this.startDay = startDay
    this.range = GanttParameterVO.clampRange(
      range.start,
      range.end,
      'start',
      unit
    )
    this.displayOptions = {
      ...GanttParameterVO.defaultDisplayOptions(),
      ...displayOptions,
    }
  }

  public static defaultDisplayOptions(): GanttDisplayOptions {
    return {
      showTypeIcon: true,
      showProgressRate: true,
      showName: true,
      showAssignee: true,
      showStatus: true,
      showProgressLine: false,
      showMilestoneName: true,
      milestoneNameResetSeq: 0,
      lockRange: false,
    }
  }

  public static defaultValue = (): GanttParameterVO => {
    return new GanttParameterVO(GanttDisplayUnit.MONTH, DayOfWeek.MONDAY, {
      start: new DateVO().subtractWeeks(10),
      end: new DateVO().addWeeks(10),
    })
  }

  public serialize = (): SerializedGanttParameter => {
    return {
      unit: this.unit,
      startDay: this.startDay,
      range: {
        start: this.range.start.toNumberValue(),
        end: this.range.end.toNumberValue(),
      },
      displayOptions: this.displayOptions,
    }
  }

  public static deserialize = (
    source: SerializedGanttParameter
  ): GanttParameterVO => {
    return new GanttParameterVO(
      source.unit,
      source.startDay,
      {
        start: new DateVO(source.range.start),
        end: new DateVO(source.range.end),
      } as DisplayRange,
      source.displayOptions
    )
  }

  public static roundStartDate(
    date: DateVO,
    unit: GanttDisplayUnit,
    startDay: DayOfWeek,
    fiscalStartMonth: number = 1
  ): DateVO {
    if (unit === GanttDisplayUnit.WEEK) {
      const d = date.toDate()
      const diff = (d.getDay() - mapDayOfWeekToNumber(startDay) + 7) % 7
      return date.subtractDays(diff)
    }
    if (unit === GanttDisplayUnit.MONTH) return date.getFirstDayOfMonth()
    if (unit === GanttDisplayUnit.QUARTER) {
      return date.getFirstDayOfQuarter(fiscalStartMonth)
    }
    return date
  }

  public static roundEndDate(
    date: DateVO,
    unit: GanttDisplayUnit,
    startDay: DayOfWeek,
    fiscalStartMonth: number = 1
  ): DateVO {
    if (unit === GanttDisplayUnit.WEEK) {
      const d = date.toDate()
      const diff = (mapDayOfWeekToNumber(startDay) - d.getDay() + 6) % 7
      return date.addDays(diff)
    }
    if (unit === GanttDisplayUnit.MONTH) return date.getLastDayOfMonth()
    if (unit === GanttDisplayUnit.QUARTER) {
      return date.getLastDayOfQuarter(fiscalStartMonth)
    }
    return date
  }

  public static incrementDate(
    date: DateVO,
    unit: GanttDisplayUnit,
    direction: 1 | -1
  ): DateVO {
    switch (unit) {
      case GanttDisplayUnit.DAY:
        return date.addDays(direction)
      case GanttDisplayUnit.WEEK:
        return date.addWeeks(direction)
      case GanttDisplayUnit.MONTH:
        return date.addMonths(direction)
      case GanttDisplayUnit.QUARTER:
        return date.addQuarters(direction)
    }
  }

  public static shiftRange(
    range: DisplayRange,
    unit: GanttDisplayUnit,
    startDay: DayOfWeek,
    direction: 1 | -1,
    fiscalStartMonth: number = 1
  ): DisplayRange {
    const newStart = GanttParameterVO.roundStartDate(
      GanttParameterVO.incrementDate(range.start, unit, direction),
      unit,
      startDay,
      fiscalStartMonth
    )
    const newEnd = GanttParameterVO.roundEndDate(
      GanttParameterVO.incrementDate(range.end, unit, direction),
      unit,
      startDay,
      fiscalStartMonth
    )
    const anchor = direction === -1 ? 'start' : 'end'
    return GanttParameterVO.clampRange(newStart, newEnd, anchor, unit)
  }

  public static clampRange(
    start: DateVO,
    end: DateVO,
    anchor: 'start' | 'end',
    unit: GanttDisplayUnit
  ): DisplayRange {
    const limit = GanttParameterVO.getMaxDays(unit)
    const diffDays = end.diff(start)
    if (diffDays <= limit) {
      return { start, end }
    }
    if (anchor === 'start') {
      return { start, end: start.addDays(limit) }
    }
    return { start: end.subtractDays(limit), end }
  }

  public static isMonthOrQuarterUnit(unit: GanttDisplayUnit): boolean {
    return unit === GanttDisplayUnit.MONTH || unit === GanttDisplayUnit.QUARTER
  }

  public static isAtMaxRange(
    range: DisplayRange,
    unit: GanttDisplayUnit
  ): boolean {
    return range.end.diff(range.start) >= GanttParameterVO.getMaxDays(unit)
  }

  private static readonly MAX_RANGE_BY_UNIT: Record<
    GanttDisplayUnit,
    { days: number; years: number }
  > = {
    [GanttDisplayUnit.DAY]: { days: 365, years: 1 },
    [GanttDisplayUnit.WEEK]: { days: 365, years: 1 },
    [GanttDisplayUnit.MONTH]: { days: MAX_DISPLAY_RANGE_DAYS, years: 5 },
    [GanttDisplayUnit.QUARTER]: { days: MAX_DISPLAY_RANGE_DAYS, years: 5 },
  }

  private static getMaxDays(unit: GanttDisplayUnit): number {
    return GanttParameterVO.MAX_RANGE_BY_UNIT[unit].days
  }

  /** 表示期間の上限（年）。上限を告知するメッセージの埋め込み用 */
  public static getMaxYears(unit: GanttDisplayUnit): number {
    return GanttParameterVO.MAX_RANGE_BY_UNIT[unit].years
  }

  /** その表示単位に切り替えると表示期間が短縮されるか */
  public static requiresRangeShortening(
    range: DisplayRange,
    unit: GanttDisplayUnit
  ): boolean {
    return range.end.diff(range.start) > GanttParameterVO.getMaxDays(unit)
  }

  public calcNearestPosition = (): 'start' | 'middle' | 'end' => {
    // Get position for ag-grid ensureColumnVisible api
    const today = DateVO.now()
    const todayFromStart = today.diff(this.range.start)
    if (todayFromStart < 0) return 'start' // Today is before start
    if (this.range.end.diff(today) < 0) return 'end' // Today is after end

    const oneThirds = Math.floor(this.range.end.diff(this.range.start) / 3)
    if (todayFromStart < oneThirds) return 'start'
    if (todayFromStart < oneThirds * 2) return 'middle'
    return 'end'
  }
}
